import React, { useEffect, useState } from "react";
import { Download, X, DeviceMobile, Share, Plus } from "@phosphor-icons/react";

const DISMISS_KEY = "irs_install_dismissed_v1";

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isMobileOrTablet() {
  const ua = window.navigator.userAgent || "";
  return /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(ua) ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 1);
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS does NOT fire beforeinstallprompt — show our own tooltip after a delay
    if (isIOS() && isMobileOrTablet()) {
      const t = setTimeout(() => setShowIOS(true), 2500);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", handler); };
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice?.outcome === "accepted") {
      setShow(false);
    }
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
    setShowIOS(false);
  };

  if (show && deferred) {
    return (
      <div className="fixed inset-x-3 bottom-3 md:left-auto md:right-6 md:w-[360px] z-50 no-print rounded-sm border border-zinc-300 bg-white shadow-lg p-4 flex items-start gap-3" data-testid="pwa-install-banner">
        <div className="w-10 h-10 rounded-sm bg-indigo-950 text-white grid place-items-center shrink-0"><DeviceMobile className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-zinc-900 text-sm">Install Inventory Reconciliation</div>
          <div className="text-xs text-zinc-600 mt-0.5">Add to home screen for faster access and offline counting on phone, tablet or laptop.</div>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={install} className="inline-flex items-center gap-1.5 rounded-sm bg-indigo-950 hover:bg-indigo-900 text-white px-3 py-1.5 text-xs font-medium" data-testid="pwa-install-button">
              <Download className="w-3.5 h-3.5" /> Install app
            </button>
            <button onClick={dismiss} className="text-xs text-zinc-600 hover:text-zinc-900" data-testid="pwa-dismiss-button">Not now</button>
          </div>
        </div>
        <button onClick={dismiss} className="text-zinc-400 hover:text-zinc-700 shrink-0" aria-label="Dismiss"><X className="w-4 h-4" /></button>
      </div>
    );
  }

  if (showIOS) {
    return (
      <div className="fixed inset-x-3 bottom-3 md:left-auto md:right-6 md:w-[360px] z-50 no-print rounded-sm border border-zinc-300 bg-white shadow-lg p-4 flex items-start gap-3" data-testid="pwa-ios-banner">
        <div className="w-10 h-10 rounded-sm bg-indigo-950 text-white grid place-items-center shrink-0"><DeviceMobile className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-zinc-900 text-sm">Install on iPhone / iPad</div>
          <ol className="mt-1 text-xs text-zinc-700 space-y-1">
            <li>1. Tap the <span className="inline-flex items-center gap-0.5 align-middle"><Share className="w-3.5 h-3.5" /> Share</span> icon in Safari.</li>
            <li>2. Choose <span className="inline-flex items-center gap-0.5 align-middle"><Plus className="w-3.5 h-3.5" /> Add to Home Screen</span>.</li>
            <li>3. Open from your Home Screen for offline counting.</li>
          </ol>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={dismiss} className="text-xs text-zinc-600 hover:text-zinc-900" data-testid="pwa-ios-dismiss">Got it</button>
          </div>
        </div>
        <button onClick={dismiss} className="text-zinc-400 hover:text-zinc-700 shrink-0" aria-label="Dismiss"><X className="w-4 h-4" /></button>
      </div>
    );
  }

  return null;
}
